import { useEffect, useState } from 'react';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import classes from './ImpersonationOverlay.module.scss';
import { endImpersonation, getImpersonationSession, remainingMs } from '../../../services/impersonationSession';
import { stopImpersonation } from '../../../services/impersonationService';

const formatRemaining = ms => {
    const seconds = Math.max(0, Math.floor(ms / 1000));
    return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
};

/**
 * The red frame and bar of an impersonation tab (#790): who is being viewed, that it is read-only, the time left
 * and Stop. Mounted above the route tree so it also covers error and not-found pages. It is a reminder, not a
 * control: the API enforces the session.
 */
export default function ImpersonationOverlay() {
    const session = getImpersonationSession();

    return session ? <ImpersonationBar session={session} /> : null;
}

function ImpersonationBar({ session }) {
    const { t } = useTranslation('impersonation');
    const [left, setLeft] = useState(() => remainingMs(session));
    const [stopping, setStopping] = useState(false);

    useEffect(() => {
        document.body.classList.add('impersonating');
        const timer = setInterval(() => {
            const ms = remainingMs(session);
            setLeft(ms);
            if (ms <= 0) {
                clearInterval(timer);
                endImpersonation('expired');
            }
        }, 1000);

        return () => {
            clearInterval(timer);
            document.body.classList.remove('impersonating');
        };
    }, [session]);

    const onStop = async () => {
        setStopping(true);
        await stopImpersonation();
    };

    return (
        <>
            <div className={classes.frame} aria-hidden="true" />
            <div className={classes.bar} role="status" data-testid="impersonation-bar">
                <span className={classes.who}>
                    {t('overlay.viewingAs', { name: session.target?.name, email: session.target?.email })}
                </span>
                <span className={classes.badge}>{t('overlay.readOnly')}</span>
                <span className={classes.countdown} aria-label={t('overlay.timeLeft')}>
                    {formatRemaining(left)}
                </span>
                <Button size="small" danger ghost={false} onClick={onStop} loading={stopping}>
                    {t('overlay.stop')}
                </Button>
            </div>
        </>
    );
}
